import {format, subSeconds} from 'date-fns';
import type {FoodMenuItem} from '~/types/foodMenu';
import {getCommonStoryParams} from '~/storyblok/api/options';

export const useFoodMenuStore = defineStore('foodMenu', () => {
	const {$storyblokApi, runWithContext} = useNuxtApp();
	const menuList = ref<Record<string, FoodMenuItem | null>>({});
	const fetchPromises: Record<string, Promise<void> | undefined> = {};
	const abortControllers: Record<string, AbortController> = {};

	async function fetchMenu(targetFolder: string, validFrom: Date) {
		if (menuList.value[targetFolder] !== undefined) {
			return;
		}

		if (fetchPromises[targetFolder]) {
			return await fetchPromises[targetFolder];
		}

		const menuType = targetFolder.split('/').pop() || 'default';

		if (abortControllers[menuType]) {
			abortControllers[menuType].abort();
		}

		const controller = new AbortController();
		abortControllers[menuType] = controller;

		// Subtract 1 second from the validFrom because the API uses a greater than filter, not greater than or equal to
		const modifiedValidFrom = subSeconds(validFrom, 1);

		fetchPromises[targetFolder] = fetchPromises[targetFolder] ?? $storyblokApi
			.get(
				'stories/',
				{
					...runWithContext(getCommonStoryParams),
					starts_with: targetFolder,
					content_type: 'RestaurantWeekMenu',
					per_page: 1,
					is_startpage: false,
					filter_query: {dataFrom: {gt_date: format(modifiedValidFrom, 'yyyy-MM-dd HH:mm:ss')}},
					sort_by: 'content.dataFrom:asc',
				},
				{signal: controller.signal},
			)
			.then(({data}) => {
				menuList.value = {
					...menuList.value,
					[targetFolder]: data.stories.length
						? {
							folder: targetFolder,
							menu: data.stories[0].content,
						}
						: null,
				};
			})
			.catch((e: any) => {
				if (e.name === 'AbortError' || e.code === 'ERR_CANCELED' || e.message === 'canceled') {
					return;
				}
				throw createError({
					message: 'Failed to fetch food menu',
					data: {
						targetFolder,
						validFrom,
					},
					cause: e,
				});
			})
			.finally(() => {
				delete fetchPromises[targetFolder];
			});

		return await fetchPromises[targetFolder];
	}

	return {
		menuList,
		fetchMenu,
	};
});
